<template>
  <div v-if="requirement != null">
    
    <div class="flex items-center">
      <div class="text-lg mr-4">
        Requirements - {{ getApplicantName(getClientPrimaryApplicant) }}
      </div>

      <NoteIcon page="requirements" />
    </div>
    
    <div class="my-8">
        
      <div class="p-4 bg-yellow-200 text-sm overflow-hidden rounded border-yellow-400 w-1/2">
        <div class="float-left" style="margin-top:5px;">
          <img src="https://cdn2.iconfinder.com/data/icons/officeicons/PNG/48/Help.png" class="w-4" />
        </div>

        <div class="ml-6 leading-normal">
          Banks require that all amounts (e.g. income, assets liabilities) on your application are in Australian dollars.
        </div>
      </div>

      <div class="label-line w-full lg:w-1/2">
        <div class="label-standard">
          <RequiredAsterisk />
          What is your primary reason for seeking credit?
        </div>  
        
        <div class="form-line">
          <select class="select-standard"
            :value="requirement.primary_reason != null ? requirement.primary_reason : -1"
            v-on:change="updateValue({
              objectType: 'requirement',
              key       : 'primary_reason', 
              value     : $event.target.value
            })">
            <option value="-1" disabled>Select an option...</option>
            <option value="0">Buy Home</option>
            <option value="1">Debt Consolidate</option>
            <option value="2">Refinance</option>
            <option value="3">Buy Investment Property / Granny Flat</option>
            <option value="4">Renovation / Cash Out</option>
            <option value="5">Buy Land</option>
            <option value="7">Land &amp; Construction</option>
            <option value="6">Other</option>
          </select>
        </div>
      </div>

      <template v-if="requirement.primary_reason == 0 || requirement.primary_reason == 3 || requirement.primary_reason == 7">
        <div class="label-line w-full lg:w-1/2">
          <div class="label-standard">
            <RequiredAsterisk />

            Is everyone in this loan inquiry a first home buyer?
          </div>

          <div class="form-line flex items-center">
            <InputRadio 
              name="inputFirstHomeBuyer"
              id="inputFirstHomeBuyerYes"
              propertyKey="first"
              value="1"
              :checked="requirement.first == 1"
              :updateValue="updateValue" />

            <label for="inputFirstHomeBuyerYes" class="text-base ml-2 mr-8">Yes</label>

            <InputRadio 
              name="inputFirstHomeBuyer"
              id="inputFirstHomeBuyerNo"
              propertyKey="first"
              value="0"
              :checked="requirement.first == 0"
              :updateValue="updateValue" />

            <label for="inputFirstHomeBuyerNo" class="text-base ml-2">No</label>
          </div>
        </div>
      </template>


      <div class="label-line w-full lg:w-1/2">
        <InputDollarValue 
          label="How much would you like to borrow?"
          propertyKey="borrow"
          objectType="requirement"
          :value="requirement.borrow"
          :updateValue="updateValue"
          :required="true" />
      </div>

      <template v-if="requirement.primary_reason == 0 || requirement.primary_reason == 3 || requirement.primary_reason == 5 || requirement.primary_reason == 7">
        <div class="label-line w-full lg:w-1/2">
          <InputDollarValue 
            label="How much do you wish to spend on the property?"
            propertyKey="spend"
            :value="requirement.spend"
            :updateValue="updateValue"/>
        </div>
      </template>

      <template v-if="requirement.primary_reason == 0 || requirement.primary_reason == 3 || requirement.primary_reason == 5 || requirement.primary_reason == 7">
        <div class="label-line w-full lg:w-1/2">
          <StateSelector 
            propertyKey="spend_state_id"
            :value="requirement.spend_state_id"
            :updateValue="updateValue" />
        </div>

        <div class="label-line w-full lg:w-1/2">
          <div class="label-standard">
            Maximum stamp duty payable
          </div>

          <div class="mt-1 text-gray-700">
            This may be less depending on your situation.  Speak to your broker for a more accurate calculation.
          </div>

          <div class="mt-2 bg-gray-200 border border-gray-600" style="height:42px;padding:10px;">
            <template v-if="fetchingStampDuty">
              <span class="text-xs mr-4">Calculating stamp duty</span>
              <img src="/static/img/ajax-loader.gif" style="height:8px;" class="inline" />
            </template>

            <template v-else>
              <div class="text-sm">$ {{ stampDutyPayable }}</div>
            </template>
          </div>
        </div>
      </template>

      <div class="label-line w-full lg:w-1/2">
        <TextareaField 
          label="How will any extra funds be used?"
          propertyKey="extra_funds"
          :value="requirement.extra_funds"
          :updateValue="updateValue"/>
      </div>

      <div class="label-line label-standard">
        If purchasing property, how long are you looking to retain the property?
      </div>

      <div class="flex flex-wrap mt-2">
        <div class="w-full lg:w-1/2 pr-0 lg:pr-4 mb-10 lg:mb-0">
          <select class="select-standard"
            :value="requirement.how_long == null ? -1 : requirement.how_long"
            v-on:change="updateValue({
              objectType: 'requirement',
              key       : 'how_long', 
              value     : $event.target.value
            })">
            <option value="-1" disabled>Select an option...</option>
            <option value="0">Not Purchasing Property</option>
            <option value="5">0-2 Years</option>
            <option value="1">2 Years</option>
            <option value="2">2-5 Years</option>
            <option value="3">5-10 Years</option>
            <option value="4">10 Years Plus</option>
          </select>
        </div>
      </div>

      <template v-if="requirement.primary_reason == 1 || requirement.primary_reason == 2">
        <div class="mt-8 mb-8">
          <TextareaField 
            label="If you are refinancing and or consolidating debt, please provide information on what your objectives are for seeking this."
            propertyKey="benefits"
            :value="requirement.benefits"
            :updateValue="updateValue"/>
        </div>
      </template>

      <template v-if="requirement.primary_reason == 0 || requirement.primary_reason == 3 || requirement.primary_reason == 5 || requirement.primary_reason == 7">
        <div class="label-line">
          <Solicitor></Solicitor>
        </div>
      </template>

      <div class="label-line label-standard">
        Do you have a preference for the type of lender that you would like to deal with? 
      </div>

      <div class="flex flex-wrap mt-2">
        <div class="w-full lg:w-1/2 pr-0 lg:pr-4 mb-10 lg:mb-0">
          <select class="select-standard"
            :value="requirement.bank_preference == null ? -1 : requirement.bank_preference"
            v-on:change="updateValue({
              objectType: 'requirement',
              key       : 'bank_preference', 
              value     : $event.target.value
            })">
            <option value="-1" disabled>Select an option...</option>
            <option value="major bank">Major Bank</option>
            <option value="smaller bank">Smaller Bank</option>
            <option value="regional bank">Regional Bank</option>
            <option value="credit union">Credit Union</option>
            <option value="non-bank lender">Non-bank lender</option>
            <option value="no preference">I don't mind</option>
            <option value="unsure">Unsure and would like to discuss</option>
          </select>
        </div>
      </div>

      <div class="label-line label-standard">
        So I can provide you with a choice of lenders and loan options which meet your requirements and preferences, <br>
        please rank your top three priorities that are important to you in a lender:
      </div>

      <div class="flex flex-wrap label-line">
        <div class="label-standard pt-2 pr-2">
          1st Priority
        </div>
        <div class="w-full lg:w-1/2 pr-0 lg:pr-3">
          <select class="select-standard"
            :value="requirement.first_priority_lender == null ? -1 : requirement.first_priority_lender"
            v-on:change="updateValue({
              objectType: 'requirement',
              key       : 'first_priority_lender', 
              value     : $event.target.value
            })">
            <option value="-1" disabled>Select an option...</option>
            <option value="amount of money">The lender who will loan me/us the most amount of money</option>
            <option value="cheapest interest rate">The lender who has the cheapest interest rate</option>
            <option value="best loan features">The lender with the best loan features</option>
            <option value="major lender">A major lender</option>
            <option value="small lender">A small lender</option>
            <option value="regional lender">A regional lender</option>
            <option value="non-bank lender">A non-bank lender</option>
            <option value="branch network">Access to a branch network</option>
            <option value="customer service ethic">A lender with a strong customer service ethic</option>
            <option value="environmental footprint">A lender who has strong ethics and a commitment to their environmental footprint</option>          
            <option value="other">Other</option>          
          </select>
        </div>
      </div>

      <template v-if="requirement.first_priority_lender == 'other'">
        <div class="label-line w-full lg:w-1/2">
          <TextareaField 
            label="Please provide information about what this is."
            propertyKey="first_priority_lender_other"
            :value="requirement.first_priority_lender_other"
            :updateValue="updateValue"/>
        </div>
      </template>

      <div class="flex flex-wrap label-line">
        <div class="label-standard pt-2 pr-2">
          2nd Priority
        </div>
        <div class="w-full lg:w-1/2 pr-0 lg:pr-3">
          <select class="select-standard"
            :value="requirement.second_priority_lender == null ? -1 : requirement.second_priority_lender"
            v-on:change="updateValue({
              objectType: 'requirement',
              key       : 'second_priority_lender', 
              value     : $event.target.value
            })">
            <option value="-1" disabled>Select an option...</option>
            <option value="amount of money">The lender who will loan me/us the most amount of money</option>
            <option value="cheapest interest rate">The lender who has the cheapest interest rate</option>
            <option value="best loan features">The lender with the best loan features</option>
            <option value="major lender">A major lender</option>
            <option value="small lender">A small lender</option>
            <option value="regional lender">A regional lender</option>
            <option value="non-bank lender">A non-bank lender</option>
            <option value="branch network">Access to a branch network</option>
            <option value="customer service ethic">A lender with a strong customer service ethic</option>
            <option value="environmental footprint">A lender who has strong ethics and a commitment to their environmental footprint</option>          
            <option value="other">Other</option>          
          </select>
        </div>
      </div>

      <template v-if="requirement.second_priority_lender === 'other'">
        <div class="label-line w-full lg:w-1/2">
          <TextareaField 
            label="Please provide information about what this is."
            propertyKey="second_priority_lender_other"
            :value="requirement.second_priority_lender_other"
            :updateValue="updateValue"/>
        </div>
      </template>

      <div class="flex flex-wrap label-line">
        <div class="label-standard pt-2 pr-2">
          3rd Priority
        </div>
        <div class="w-full lg:w-1/2 pr-0 lg:pr-3">
          <select class="select-standard"
            :value="requirement.third_priority_lender == null ? -1 : requirement.third_priority_lender"
            v-on:change="updateValue({
              objectType: 'requirement',
              key       : 'third_priority_lender', 
              value     : $event.target.value
            })">
            <option value="-1" disabled>Select an option...</option>
            <option value="amount of money">The lender who will loan me/us the most amount of money</option>
            <option value="cheapest interest rate">The lender who has the cheapest interest rate</option>
            <option value="best loan features">The lender with the best loan features</option>
            <option value="major lender">A major lender</option>
            <option value="small lender">A small lender</option>
            <option value="regional lender">A regional lender</option>
            <option value="non-bank lender">A non-bank lender</option>
            <option value="branch network">Access to a branch network</option>
            <option value="customer service ethic">A lender with a strong customer service ethic</option>
            <option value="environmental footprint">A lender who has strong ethics and a commitment to their environmental footprint</option>          
            <option value="other">Other</option>          
          </select>
        </div>
      </div>

      <template v-if="requirement.third_priority_lender === 'other'">
        <div class="label-line w-full lg:w-1/2">
          <TextareaField 
            label="Please provide information about what this is."
            propertyKey="third_priority_lender_other"
            :value="requirement.third_priority_lender_other"
            :updateValue="updateValue"/>
        </div>
      </template>

      <div class="label-line w-full lg:w-1/2">
        <TextareaField 
          label="Are there any particular lenders you would prefer to use?"
          propertyKey="lender_preference"
          :value="requirement.lender_preference"
          :updateValue="updateValue"/>
      </div>

      <div class="label-line label-standard">
        Which of the following loan features are of interest? (Your broker can explain these in more detail if necessary)
      </div>

      <div class="border border-gray-300er mt-4 p-4 bg-gray-200 w-full lg:w-1/2">
        <LoanFeature 
          label="Fixed rate loan"
          propertyKey="fixed_rate"
          :value="requirement.fixed_rate"
          :updateValue="updateValue"
          tooltip="Offers the certainty of knowing what your repayments are for a fixed period, and safeguards you against increases in interest rates."></LoanFeature>
      </div>

      <div class="border border-gray-300er mt-2 p-4 bg-gray-200 w-full lg:w-1/2">
        <LoanFeature 
          label="Interest only loan"
          propertyKey="interest_only"
          :value="requirement.interest_only"
          :updateValue="updateValue"
          tooltip="You only pay the interest on the loan but not the principal. Typically used for investments."></LoanFeature>
      </div>

      <div class="border border-gray-300er mt-2 p-4 bg-gray-200 w-full lg:w-1/2">
        <LoanFeature 
          label="Fixed &amp; variable"
          propertyKey="fixed_variable"
          :value="requirement.fixed_variable"
          :updateValue="updateValue"
          tooltip="You can fix the interest rate on part of the loan and still make additional payments on the variable part of the loan."></LoanFeature>
      </div>

      <div class="border border-gray-300er mt-2 p-4 bg-gray-200 w-full lg:w-1/2">
        <LoanFeature 
          label="Additional repayments"
          propertyKey="additional"
          :value="requirement.additional"
          :updateValue="updateValue"></LoanFeature>
      </div>

      <div class="border border-gray-300er mt-2 p-4 bg-gray-200 w-full lg:w-1/2">
        <LoanFeature 
          label="Offset"
          propertyKey="offset"
          :value="requirement.offset"
          :updateValue="updateValue"
          tooltip="Your loan is linked to a transaction account so that any money deposited there will help to reduce accumulated interest."></LoanFeature>
      </div>

      <div class="border border-gray-300er mt-2 p-4 bg-gray-200 w-full lg:w-1/2">
        <LoanFeature 
          label="Re-draw facility"
          propertyKey="redraw"
          :value="requirement.redraw"
          :updateValue="updateValue"></LoanFeature>
      </div>

      <div class="label-line w-full lg:w-1/2">
        <TextareaField 
          label="Other features sought?"
          propertyKey="other"
          :value="requirement.other"
          :updateValue="updateValue"/>
      </div>


      <div class="label-line label-standard">
        As far as you are aware, is your credit history clear?
      </div>

      <div class="form-line flex items-center">
        <InputRadio 
          name="inputCredit"
          id="inputCreditYes"
          propertyKey="credit"
          value="1"
          :checked="requirement.credit == 1"
          :updateValue="updateValue" />

        <label class="text-base ml-2 mr-8" for="inputCreditYes">Yes</label>

        <InputRadio 
          name="inputCredit"
          id="inputCreditNo"
          propertyKey="credit"
          value="0"
          :checked="requirement.credit == 0"
          :updateValue="updateValue" />

        <label class="text-base ml-2" for="inputCreditNo">No</label>
      </div>

      <template v-if="requirement.credit == 0">
        <div class="label-line">
          <TextareaField 
            label="Please provide more information"
            propertyKey="credit_text"
            :value="requirement.credit_text"
            :updateValue="updateValue"/>
        </div>
      </template>
      
      <div class="button-line">
        <ApplicationButton />
      </div>
    </div>
  </div>
</template>

<script>
  import InputRadio         from '@/shared/components/form/InputRadio'
  import InputText          from '@/shared/components/form/InputText'
  import RequiredAsterisk   from '@/shared/components/form/RequiredAsterisk'
  import InputDollarValue   from '@/shared/components/form/InputDollarValue'
  import TitleSelector      from '@/shared/components/form/TitleSelector'
  import StateSelector      from '@/shared/components/form/StateSelector'
  import TextareaField      from '@/shared/components/form/TextareaField'
  import LoanFeature        from './LoanFeature'
  import Solicitor          from './Solicitor'
  import { EventBus }       from '@/shared/event-bus'
  import ApplicationButton  from '@/shared/components/form/ApplicationButton'
  import NoteIcon           from '@/shared/components/NoteIcon'

  export default {

    components: {
      NoteIcon,
      InputRadio,
      InputText,
      RequiredAsterisk,
      InputDollarValue,
      TextareaField,
      StateSelector,
      LoanFeature,
      TitleSelector,
      Solicitor,
      ApplicationButton
    },

    data () {
      return {
        client     : null,
        requirement: null,
        comment    : null,

        // stampDutyPayable is a value that is automatically calculated
        // as the user types in the amount of money they want to spend on property
        fetchingStampDuty: false,
        stampDutyPayable : null,

        // since users are typing this value, we should be able to cancel the previous ajax call
        stampDutyAjax: null 
      }
    },

    created() {
      this.logActivity({
        type        : 'requirement-visited',
        clientId    : this.$route.params.clientId,
        currentView : this.$store.state.view
      })

      this.showLoader("Loading requirements...")
      this.apiGet({
        clientId : this.$route.params.clientId
      }, 'requirement/detail', (requirement) => {
        this.hideLoader()
        this.requirement = requirement
        
        // set some default values in case there are null values
        this.requirement.primary_reason = this.requirement.primary_reason != null ? this.requirement.primary_reason : -1
        this.requirement.first          = this.requirement.first          != null ? this.requirement.first          : -1
        this.requirement.how_long        = this.requirement.how_long        != null ? this.requirement.how_long        : -1

        // let's also calculate the stamp duty
        this.calculateStampDuty(this.requirement.spend)
      })
    },

    methods : {
      // when users type in the amount of money they want to spend on
      // property, we automatically calculate the stamp duty, which 
      // depends on state
      calculateStampDuty(value) {

        if(value != null && this.requirement.spend_state_id != null) {
          this.fetchingStampDuty = true

          // also get the requirement of the current client
          this.apiGet({
            clientId: this.$store.state[this.$store.state.view + 'Module'].client.id,
            stateId : this.requirement.spend_state_id,
            value            
          }, 'requirement/stamp-duty', (data) => {
            this.fetchingStampDuty = false
            this.stampDutyPayable  = data.stampDuty            
          })
        }

        else {
          this.stampDutyPayable = null
        }
      },

      // a method that will receive the value and update
      updateValue(params) {
        this.updateRequirement(params.key, params.value)

        // note that when the key is spend_state_id, we also need to re-calculate
        // the stamp duty value, so let's do that here
        if(params.key == 'spend' || params.key == 'spend_state_id') {
          this.calculateStampDuty(this.requirement.spend)
        }
      },

      updateRequirement(key, value) {
        if(this.requirement[key] != value) {
          EventBus.$emit('set-autosave', true)
          this.$set(this.requirement, key, value)
          this.apiPost({
            clientId : this.$route.params.clientId,
            key,
            value            
          }, 'requirement/save', () => {
            EventBus.$emit('set-autosave', false)
          })
        }
      },
    },

    metaInfo () {
      return {
        title: 'Requirements - ' + (this.getClientPrimaryApplicant != null ? this.getApplicantName(this.getClientPrimaryApplicant) : '') + " | Brokerpad"
      }
    }
  }
</script>